import React from 'react'
import { css } from '@emotion/core'
import { Card, colors, Row, Bleed, DataBlock } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { type SearchResult as SearchResultType, type NativeDragApi } from '../types'

const contentCss = css`
  display: flex;
  height: 180px;
  width: 400px;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  cursor: grab;
`

const circleContainerCss = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-inline-end: 16px;
  pointer-events: none;
`

const strikethroughCss = css`
  text-decoration: line-through;
  color: ${colors.red70};
`

const ovalCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  min-width: 50px;
  height: 50px;
  border-radius: 25px;
  padding: 0 12px;
  font-weight: bold;
  flex-shrink: 0;
  gap: 4px;
`

const arrowCss = css`
  color: ${colors.gray70};
  font-size: 14px;
`

const getCardColor = (originalIndex: number): string => {
  const colorSequence = ['yellow', 'green', 'blue', 'purple', 'red']
  const shadeSequence = [20, 30, 40]
  const itemsPerColor = 3

  const colorGroupIndex = Math.floor((originalIndex - 1) / itemsPerColor)
  const color = colorSequence[colorGroupIndex % colorSequence.length]

  const shadeIndex = (originalIndex - 1) % itemsPerColor
  const shade = shadeSequence[shadeIndex]

  const colorKey = `${color}${shade}` as keyof typeof colors
  const selectedColor = colors[colorKey]
  return typeof selectedColor === 'string' ? selectedColor : colors.gray30
}

type SearchResultProps = Readonly<{
  result: SearchResultType
  dragIndex: number
  nativeDragApi: NativeDragApi
  totalItems: number
  isDraggable?: boolean
}>

const SearchResult = ({ result, dragIndex, nativeDragApi, isDraggable = true }: SearchResultProps) => {
  const [t] = useTranslation()
  const cardColor = getCardColor(result.originalIndex)
  const draggableProps = isDraggable ? { draggable: true, 'drag-index': dragIndex, ...nativeDragApi } : {}
  const isReordered = result.originalIndex !== dragIndex + 1

  return (
    <Row alignX="start" alignY="middle">
      <Card backgroundColor={cardColor} maxWidth={400} margins="sm">
        <Bleed all>
          <div css={contentCss} {...draggableProps}>
            <div css={circleContainerCss}>
              <div css={ovalCss}>
                {isDraggable && isReordered ? (
                  <>
                    <span css={strikethroughCss}>{result.originalIndex}</span>
                    <span css={arrowCss}>→</span>
                    <span>{dragIndex + 1}</span>
                  </>
                ) : (
                  <span>{dragIndex + 1}</span>
                )}
              </div>
            </div>
            <div style={{ pointerEvents: 'none', flexGrow: 1 }}>
              <DataBlock
                size="sm"
                heading={result?.displayName}
                as="p"
                data={
                  <>
                    <br />
                    <strong>{t('place-learn-to-rank-page.search-result.id.label', 'ID:')} </strong>
                    {result.id}
                    <br />
                    <strong>{t('place-learn-to-rank-page.search-result.raw-score.label', 'Raw Score:')} </strong>
                    {result?.rawScore}
                    <br />
                    <strong>
                      {t('place-learn-to-rank-page.search-result.relevance-score.label', 'Relevance Score:')}{' '}
                    </strong>
                    {result?.relevanceScore}
                    <br />
                    <strong>{t('place-learn-to-rank-page.search-result.full-name.label', 'Full displayName:')} </strong>
                    {result?.fullDisplayName}
                    <br />
                  </>
                }
              />
            </div>
          </div>
        </Bleed>
      </Card>
    </Row>
  )
}

export default SearchResult
